import React, { createContext, useContext, useState, useEffect } from 'react';

type PerformanceMode = 'performance' | 'beauty';

interface PerformanceContextType {
  performanceMode: PerformanceMode;
  togglePerformanceMode: () => void;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

interface PerformanceProviderProps {
  children: React.ReactNode;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ children }) => {
  const [performanceMode, setPerformanceMode] = useState<PerformanceMode>(() => {
    // Tentar recuperar do localStorage
    const savedMode = localStorage.getItem('performance-mode') as PerformanceMode | null;
    if (savedMode === 'performance' || savedMode === 'beauty') {
      return savedMode;
    }
    
    // Fallback para preferência do sistema (prefers-reduced-motion)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'performance';
    }
    
    return 'beauty';
  });

  useEffect(() => {
    // Aplicar classe no documentElement
    const root = document.documentElement;
    
    if (performanceMode === 'performance') {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
    // Persistir no localStorage
    localStorage.setItem('performance-mode', performanceMode);
  }, [performanceMode]);

  const togglePerformanceMode = () => {
    setPerformanceMode((prevMode) => (prevMode === 'performance' ? 'beauty' : 'performance'));
  };

  return (
    <PerformanceContext.Provider value={{ performanceMode, togglePerformanceMode }}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformance = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error('usePerformance deve ser usado dentro de um PerformanceProvider');
  }
  return context;
};

